import { BackgroundDots } from "@msuite/ui/background";
import { Button } from "@msuite/ui/button";
import { AuthForm, PasswordIndicator, TextInput } from "@msuite/ui/form";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import { type ConfirmPasswordSchema, confirmPasswordSchema } from "@msuite/utils/schemas";
import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { authClient } from "@/api/auth";
import { LegalFooter } from "@/components/legal-footer";

export function ResetPassword() {
	const { t } = useTranslation();
	const token = new URLSearchParams(window.location.search).get("token");
	const methods = useForm({
		resolver: resolver(
			confirmPasswordSchema.superRefine((data, context) => {
				if (data.password !== data.confirmPassword) {
					context.addIssue({
						code: "custom",
						message: "Passwords do not match",
						path: ["confirmPassword"],
					});
				}
			}),
		),
	});
	const [password] = useWatch({ control: methods.control, name: ["password"] });
	const navigate = useNavigate();

	/** Functions */
	async function handleOnSubmit(json: ConfirmPasswordSchema) {
		if (!token) return;
		await authClient.resetPassword({
			token,
			newPassword: json.password,
		});
		navigate("/");
		toast.success(t("auth.updated_password"));
	}

	/** Hooks */
	return (
		<React.Fragment>
			<BackgroundDots />
			<div className="w-screen h-screen flex-center">
				<AuthForm onSubmit={methods.handleSubmit(handleOnSubmit)}>
					<AuthForm.Header />
					<AuthForm.Body>
						<div className="v-stack">
							<TextInput
								type="password"
								label={t("auth.password")}
								isRequired
								control={methods.control}
								name="password"
								placeholder="Password"
								data-cy="sign-up-password-input"
							/>
							<PasswordIndicator currentPassword={password} />
						</div>
						<TextInput
							type="password"
							label={t("auth.confirm_password")}
							isRequired
							control={methods.control}
							name="confirmPassword"
							placeholder="Password"
							data-cy="sign-up-confirm-password-input"
						/>
					</AuthForm.Body>
					<AuthForm.Footer>
						<Button
							type="submit"
							variant="primary"
							size="sm"
							isLoading={methods.formState.isSubmitting}
						>
							{t("auth.reset_password")}
						</Button>
					</AuthForm.Footer>
				</AuthForm>
			</div>
			<LegalFooter />
		</React.Fragment>
	);
}
